import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submit", "field", "error"]
  static classes = ["loading", "error", "success"]
  
  connect() {
    this.form = this.element
    this.originalSubmitText = this.hasSubmitTarget ? this.submitTarget.textContent : null
    this.setupAutoSubmit()
    this.setupRecipeToggle()
  }

  // Auto-submit form (for dropdowns and date fields)
  autoSubmit(event) {
    event.target.form.submit()
  }

  setupAutoSubmit() {
    // Auto-submit functionality for certain form fields
    const autoSubmitFields = this.element.querySelectorAll('[data-action*="autoSubmit"]')
    autoSubmitFields.forEach(field => {
      field.addEventListener('change', this.autoSubmit)
    })
  }
  
  setupRecipeToggle() {
    const isRecipeCheckbox = this.element.querySelector('input[name*="[is_recipe]"]')
    if (isRecipeCheckbox) {
      this.toggleRecipeFields({ target: isRecipeCheckbox })
    }
  }

  toggleRecipeFields(event) {
    const isChecked = event.target.checked
    const recipeFields = this.element.querySelector('.recipe-fields')
    const articleOutlineTitle = this.element.querySelector('.article-outline-title')
    
    if (recipeFields) {
      recipeFields.style.display = isChecked ? 'block' : 'none'
    }
    
    if (articleOutlineTitle) {
      articleOutlineTitle.textContent = isChecked ? 'Recipe Article Outline' : 'Article Outline'
    }
    
    // Update placeholder text
    const titleField = this.element.querySelector('input[name*="[title]"]')
    if (titleField) {
      titleField.placeholder = isChecked ? 'Enter the proposed recipe title' : 'Enter the proposed article title'
    }
  }

  // Enhanced form submission with loading states
  submit(event) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.textContent = "Submitting..."
      
      if (this.hasLoadingClass) {
        this.submitTarget.classList.add(this.loadingClass)
      }
    }
    
    // Clear previous errors
    this.clearErrors()
  }

  // Handle successful form submission
  success(event) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      this.submitTarget.textContent = this.originalSubmitText || "Submit"
      
      if (this.hasLoadingClass) {
        this.submitTarget.classList.remove(this.loadingClass)
      }
      
      if (this.hasSuccessClass) {
        this.submitTarget.classList.add(this.successClass)
        setTimeout(() => {
          this.submitTarget.classList.remove(this.successClass)
        }, 2000)
      }
    }
  }

  // Handle form submission errors
  error(event) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      this.submitTarget.textContent = this.originalSubmitText || "Submit"
      
      if (this.hasLoadingClass) {
        this.submitTarget.classList.remove(this.loadingClass)
      }
    }
    
    // Focus on first error field
    const firstErrorField = this.form.querySelector('.field-input--error')
    if (firstErrorField) {
      firstErrorField.focus()
    }
  }

  // Clear all error states
  clearErrors() {
    this.errorTargets.forEach(error => {
      error.remove()
    })
    
    this.fieldTargets.forEach(field => {
      field.classList.remove('field-input--error')
      field.removeAttribute('aria-describedby')
    })
  }

  // Real-time validation on field blur
  validateField(event) {
    const field = event.target
    const value = field.value.trim()
    
    // Remove existing error styling
    field.classList.remove('field-input--error')
    
    // Basic validation examples
    if (field.hasAttribute('required') && !value) {
      this.showFieldError(field, 'This field is required')
      return false
    }
    
    if (field.type === 'email' && value && !this.isValidEmail(value)) {
      this.showFieldError(field, 'Please enter a valid email address')
      return false
    }
    
    if (field.type === 'number') {
      const min = field.getAttribute('min')
      const max = field.getAttribute('max')
      const numValue = parseFloat(value)
      
      if (min && numValue < parseFloat(min)) {
        this.showFieldError(field, `Value must be at least ${min}`)
        return false
      }
      
      if (max && numValue > parseFloat(max)) {
        this.showFieldError(field, `Value must be at most ${max}`)
        return false
      }
    }
    
    this.clearFieldError(field)
    return true
  }

  // Show field-specific error
  showFieldError(field, message) {
    field.classList.add('field-input--error')
    
    // Remove existing error message
    this.clearFieldError(field)
    
    // Create new error message
    const errorElement = document.createElement('span')
    errorElement.className = 'error-message'
    errorElement.textContent = message
    errorElement.setAttribute('role', 'alert')
    errorElement.setAttribute('data-form-target', 'error')
    
    // Insert after field
    field.parentNode.insertBefore(errorElement, field.nextSibling)
    
    // Update aria-describedby
    const errorId = `${field.id || field.name}-error`
    errorElement.id = errorId
    field.setAttribute('aria-describedby', errorId)
  }

  // Clear field-specific error
  clearFieldError(field) {
    const existingError = field.parentNode.querySelector('.error-message')
    if (existingError) {
      existingError.remove()
    }
    field.removeAttribute('aria-describedby')
  }

  // Email validation helper
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Auto-save functionality for forms
  autoSave(event) {
    const formData = new FormData(this.form)
    const url = this.form.getAttribute('data-autosave-url')
    
    if (!url) return
    
    fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.saved) {
        this.showAutoSaveIndicator()
      }
    })
    .catch(error => {
      console.error('Auto-save failed:', error)
    })
  }

  // Show auto-save indicator
  showAutoSaveIndicator() {
    const indicator = document.createElement('span')
    indicator.className = 'auto-save-indicator'
    indicator.textContent = 'Saved'
    indicator.style.cssText = 'color: green; font-size: 0.875rem; margin-left: 0.5rem;'
    
    if (this.hasSubmitTarget) {
      this.submitTarget.parentNode.appendChild(indicator)
      setTimeout(() => {
        indicator.remove()
      }, 2000)
    }
  }
}